// =============================================================
// PERPETUUM — module registry + manifest type.
//
// Formalizes the prototype's window.PERP_SECTIONS registry + DI bag + SecFlags
// into the binding modular architecture (PROJECT.md, REQ-modularity):
//   - Every feature is a self-contained module described by a MANIFEST
//     (id, routes, nav, requiredPermissions, dependsOn, defaultEnabled).
//   - Core depends only on the registry + contracts, never on a specific module.
//   - Modules reach each other only through declared `dependsOn`.
//   - Toggles are DATA, gated per-tenant + per-role (no redeploy).
//
// UI-less: routes/nav carry only ids + metadata, never React components — the
// platform apps (web/mobile) bind their own components to these ids.
// =============================================================

/** A navigable route a module contributes. The component is bound per-app. */
export interface ModuleRoute {
  /** Stable route id (apps map this to a screen/component). */
  id: string;
  /** URL/path pattern for web, or a logical screen name for native. */
  path: string;
  /** Permission required to access this route (subset of the module's perms). */
  requiredPermission?: string;
}

/** A sidebar/nav entry a module contributes. */
export interface ModuleNavEntry {
  /** Route id this entry points at. */
  routeId: string;
  /** i18n key for the label (resolved via `t()`), e.g. "nav.tasks". */
  labelKey: string;
  /** Logical nav group, e.g. "OVERVIEW" | "PLANNING" | "ADMIN". */
  group?: string;
  /** Icon name (resolved per-app); UI-less here. */
  icon?: string;
  /** Sort order within the group (lower = earlier). */
  order?: number;
}

/** A module manifest — the contract every feature registers with. */
export interface ModuleManifest {
  /** Globally-unique module id, e.g. "tasks", "spares", "downtimes". */
  id: string;
  /** Human-facing name (or i18n key) for admin/feature-flag UIs. */
  name: string;
  /** Semantic version of the module. */
  version?: string;
  /** Routes this module contributes. */
  routes?: ModuleRoute[];
  /** Nav entries this module contributes. */
  nav?: ModuleNavEntry[];
  /** Permission keys this module's features require. */
  requiredPermissions?: string[];
  /** Ids of modules that MUST be enabled for this one to work. */
  dependsOn?: string[];
  /** Whether the module is on by default (a tenant/role may override). */
  defaultEnabled: boolean;
}

/** Result of validating registered modules' dependency graph. */
export interface RegistryValidation {
  ok: boolean;
  /** Modules whose `dependsOn` reference an unregistered module. */
  missingDependencies: { moduleId: string; missing: string[] }[];
  /** Module ids registered more than once. */
  duplicateIds: string[];
}

/**
 * The module registry — core knows only this + the manifest contract.
 * Holds manifests; resolves enabled modules, nav, and routes for a given
 * tenant + role toggle set.
 */
export class ModuleRegistry {
  private readonly modules = new Map<string, ModuleManifest>();
  private readonly duplicates: string[] = [];

  /** Register a manifest. Duplicate ids are recorded (surfaced by `validate`). */
  register(manifest: ModuleManifest): this {
    if (this.modules.has(manifest.id)) {
      this.duplicates.push(manifest.id);
    } else {
      this.modules.set(manifest.id, manifest);
    }
    return this;
  }

  /** Register many manifests at once. */
  registerAll(manifests: ModuleManifest[]): this {
    for (const m of manifests) this.register(m);
    return this;
  }

  /** All registered manifests. */
  all(): ModuleManifest[] {
    return [...this.modules.values()];
  }

  /** A single manifest by id. */
  get(id: string): ModuleManifest | undefined {
    return this.modules.get(id);
  }

  /** Validate the dependency graph + duplicate ids. */
  validate(): RegistryValidation {
    const missingDependencies: RegistryValidation["missingDependencies"] = [];
    for (const m of this.modules.values()) {
      const missing = (m.dependsOn ?? []).filter((d) => !this.modules.has(d));
      if (missing.length > 0) {
        missingDependencies.push({ moduleId: m.id, missing });
      }
    }
    return {
      ok: missingDependencies.length === 0 && this.duplicates.length === 0,
      missingDependencies,
      duplicateIds: [...new Set(this.duplicates)],
    };
  }

  /**
   * Resolve which modules are enabled, given toggle overrides + the role's
   * permissions. A module is enabled when:
   *   - it is toggled on (explicit override, else its `defaultEnabled`), AND
   *   - the role has every `requiredPermissions`, AND
   *   - all its `dependsOn` modules are themselves enabled.
   *
   * @param overrides   per-tenant + per-role toggle map (moduleId → on/off).
   * @param permissions permission keys granted to the current role.
   */
  enabledModules(
    overrides: Record<string, boolean> = {},
    permissions: string[] = [],
  ): ModuleManifest[] {
    const permSet = new Set(permissions);
    const isToggledOn = (m: ModuleManifest): boolean =>
      overrides[m.id] ?? m.defaultEnabled;
    const hasPerms = (m: ModuleManifest): boolean =>
      (m.requiredPermissions ?? []).every((p) => permSet.has(p));

    // First pass: toggled-on + permitted.
    const candidate = new Map<string, ModuleManifest>();
    for (const m of this.modules.values()) {
      if (isToggledOn(m) && hasPerms(m)) candidate.set(m.id, m);
    }

    // Fixpoint: drop any module whose dependency isn't (transitively) enabled.
    let changed = true;
    while (changed) {
      changed = false;
      for (const m of [...candidate.values()]) {
        const depMissing = (m.dependsOn ?? []).some((d) => !candidate.has(d));
        if (depMissing) {
          candidate.delete(m.id);
          changed = true;
        }
      }
    }
    return [...candidate.values()];
  }
}

/** Convenience: a fresh registry seeded with the given manifests. */
export function createRegistry(manifests: ModuleManifest[] = []): ModuleRegistry {
  return new ModuleRegistry().registerAll(manifests);
}
